import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Activity, Loader2, CheckCircle2, AlertTriangle, XCircle, Ban, Search, Eye, Zap, TrendingUp } from "lucide-react";
import api from "../lib/api";

export const STATUS_STYLE = {
  "Successful": "bg-emerald-500/10 text-emerald-500 border-emerald-500/30",
  "Partial Success": "bg-amber-500/10 text-amber-500 border-amber-500/30",
  "No Results": "bg-sky-500/10 text-sky-400 border-sky-500/30",
  "Failed": "bg-red-500/10 text-red-500 border-red-500/30",
  "Blocked": "bg-orange-500/10 text-orange-500 border-orange-500/30",
  "Needs Review": "bg-violet-500/10 text-violet-400 border-violet-500/30",
};
export function StatusBadge({ status }) {
  return (
    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap ${STATUS_STYLE[status] || "bg-muted text-muted-foreground border-border"}`} data-testid="run-status-badge">
      {status || "—"}
    </span>
  );
}

const KPI = ({ label, value, tone }) => (
  <div className="rounded-xl border border-border bg-card px-4 py-3" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className={`text-2xl font-bold ${tone || "text-foreground"}`}>{value}</div>
    <div className="text-[11px] uppercase tracking-wider text-muted-foreground mt-0.5">{label}</div>
  </div>
);

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const fmtDur = (ms) => (ms == null ? "—" : ms < 1000 ? `${ms}ms` : ms < 60000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.round(ms / 60000)}m`);

export default function AIOperations() {
  const nav = useNavigate();
  const [summary, setSummary] = useState(null);
  const [runs, setRuns] = useState([]);
  const [facets, setFacets] = useState({});
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: "", agent_id: "", module: "", trigger: "", q: "", days: 30 });

  const loadRuns = async (f = filters) => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== "" && v != null));
    const { data } = await api.get("/ai-ops/runs", { params });
    setRuns(data.items || []);
    setFacets(data.facets || {});
  };

  useEffect(() => {
    document.title = "ARQITEK — AI Operations";
    Promise.all([api.get("/ai-ops/summary").then((r) => setSummary(r.data)), loadRuns()]).finally(() => setLoading(false));
  }, []);

  const setF = (k, v) => { const nf = { ...filters, [k]: v }; setFilters(nf); loadRuns(nf); };
  const bs = summary?.by_status || {};

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto" data-testid="ai-operations-page">
      <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1"><Activity className="h-4 w-4" /> AI Command Center</div>
      <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">AI Operations</h1>
      <p className="text-muted-foreground mt-1 text-sm mb-6">Complete visibility into what Angela and every Arqitek AI agent is doing — what they ran, why, what they found, and what to change.</p>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground py-16"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
            <KPI label="Runs Today" value={summary?.runs_today ?? 0} />
            <KPI label="Successful" value={bs["Successful"] ?? 0} tone="text-emerald-500" />
            <KPI label="Partial Success" value={bs["Partial Success"] ?? 0} tone="text-amber-500" />
            <KPI label="No Results" value={bs["No Results"] ?? 0} tone="text-sky-400" />
            <KPI label="Failed" value={bs["Failed"] ?? 0} tone="text-red-500" />
            <KPI label="Blocked" value={bs["Blocked"] ?? 0} tone="text-orange-500" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
            <KPI label="Needs Review" value={bs["Needs Review"] ?? 0} tone="text-violet-400" />
            <KPI label="Active Automations" value={summary?.active_automations ?? 0} />
            <KPI label="Leads Today" value={summary?.leads_generated_today ?? 0} />
            <KPI label="Leads This Month" value={summary?.leads_generated_month ?? 0} />
            <KPI label="Properties Analyzed" value={summary?.properties_analyzed_today ?? 0} />
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="ai-ops-filters">
            <Sel value={filters.status} onChange={(v) => setF("status", v)} label="All Statuses" options={(facets.statuses || []).map((s) => [s, s])} testid="filter-status" />
            <Sel value={filters.agent_id} onChange={(v) => setF("agent_id", v === "angela" ? "angela" : v)} label="All Agents" options={(facets.agents || []).map((a) => [a === "Angela" ? "angela" : a, a])} testid="filter-agent" />
            <Sel value={filters.module} onChange={(v) => setF("module", v)} label="All Modules" options={(facets.modules || []).map((m) => [m, m])} testid="filter-module" />
            <Sel value={filters.trigger} onChange={(v) => setF("trigger", v)} label="All Triggers" options={(facets.triggers || []).map((t) => [t, t])} testid="filter-trigger" />
            <input data-testid="filter-search" value={filters.q} onChange={(e) => setF("q", e.target.value)} placeholder="Search…" className="h-9 rounded-md border border-border bg-background px-3 text-sm" />
          </div>

          <div className="rounded-xl border border-border bg-card overflow-x-auto slim-scroll" data-testid="ai-activity-feed">
            <table className="w-full text-sm min-w-[860px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="px-4 py-3">Agent</th><th className="px-4 py-3">Task / Automation</th><th className="px-4 py-3">Module</th>
                  <th className="px-4 py-3">Trigger</th><th className="px-4 py-3">Started</th><th className="px-4 py-3">Duration</th>
                  <th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Found</th><th className="px-4 py-3 text-right">Created</th><th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {runs.length === 0 ? (
                  <tr><td colSpan={10} className="px-4 py-10 text-center text-muted-foreground">No AI runs yet. Run an AI Buy Box or add a lead to see activity here.</td></tr>
                ) : runs.map((r) => (
                  <tr key={r.id} className="border-b border-border/50 hover:bg-accent/40 cursor-pointer" onClick={() => nav(`/ai-operations/runs/${r.id}`)} data-testid={`run-row-${r.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="h-7 w-7 rounded-full bg-primary/15 text-primary flex items-center justify-center text-xs font-bold shrink-0">{r.agent_avatar || "A"}</div>
                        <div className="min-w-0"><div className="font-medium truncate">{r.agent_name}</div><div className="text-[11px] text-muted-foreground truncate">{r.agent_role}</div></div>
                      </div>
                    </td>
                    <td className="px-4 py-3"><div className="font-medium truncate max-w-[220px]">{r.task}</div><div className="text-[11px] text-muted-foreground truncate max-w-[220px]">{r.automation_name || r.buybox_name || ""}</div></td>
                    <td className="px-4 py-3 whitespace-nowrap">{r.module}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{r.trigger}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{fmtTime(r.started_at)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{fmtDur(r.duration_ms)}</td>
                    <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                    <td className="px-4 py-3 text-right">{r.results_found ?? 0}</td>
                    <td className="px-4 py-3 text-right font-medium text-emerald-500">{r.records_created_count ?? 0}</td>
                    <td className="px-4 py-3 text-right"><button className="text-primary inline-flex items-center gap-1 text-xs" data-testid={`view-run-${r.id}`}><Eye className="h-3.5 w-3.5" /> Details</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Sel({ value, onChange, label, options, testid }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="h-9 rounded-md border border-border bg-background px-2 text-sm text-foreground">
      <option value="">{label}</option>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}
